import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ShoppingCart, Plus, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PRIMARY = '#0099FF';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value || 0);

const OrderSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, API_URL } = useAuth();
  const state = location.state || {};

  const npedido = state.npedido || 'S/N';
  const fecha = state.fecha || new Date().toLocaleDateString('es-ES');
  const total = state.total;
  const embalaje = state.embalaje || false;
  const embalajeNeeded = state.embalajeNeeded || false;
  // El id del pedido para descargar el CSV. Si no vino en el state, usamos el
  // npedido cuando es numérico (npedido === id en pedidos sin numero custom).
  const orderId = state.orderId || (/^\d+$/.test(String(npedido)) ? String(npedido) : null);

  const [downloading, setDownloading] = useState(false);
  const [csvError, setCsvError] = useState('');

  const handleCsvDownload = async (embalajeParam) => {
    if (!orderId) return;
    setDownloading(true);
    setCsvError('');
    try {
      const url = embalajeParam
        ? `${API_URL}/orders/${orderId}/csv?embalaje=1`
        : `${API_URL}/orders/${orderId}/csv`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('No se pudo descargar el CSV');
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const m = disposition.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `pedido_${npedido}.csv`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setCsvError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="h-full flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md text-center space-y-6">
        {/* Green checkmark */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <Check size={40} className="text-green-600" strokeWidth={3} />
          </div>
        </div>

        {/* Title & subtitle */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">
            ¡Pedido Enviado Correctamente!
          </h1>
          <p className="text-sm text-gray-500">
            Su pedido ha sido confirmado y enviado. Recibirá un correo electrónico con los detalles.
          </p>
        </div>

        {/* Order info card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Nº Pedido</span>
            <span className="font-semibold text-gray-800">{npedido}</span>
          </div>
          <div className="border-t border-gray-100" />
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Fecha</span>
            <span className="font-medium text-gray-700">{fecha}</span>
          </div>
          {total !== undefined && total !== null && (
            <>
              <div className="border-t border-gray-100" />
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-semibold text-gray-800">{formatCurrency(total)}</span>
              </div>
            </>
          )}
        </div>

        {/* Embalaje note */}
        {embalaje && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm text-amber-700">
              Se han ajustado las cantidades al embalaje óptimo.
            </p>
          </div>
        )}

        {/* CSV download (solo si tenemos el id del pedido). Si el pedido no
            cumple embalajes, ofrecemos las dos opciones: original y embalaje. */}
        {orderId && (
          embalajeNeeded ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleCsvDownload(0)}
                disabled={downloading}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border-2 border-green-600 text-green-700 transition-colors hover:bg-green-50 disabled:opacity-60"
              >
                {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                CSV Original
              </button>
              <button
                onClick={() => handleCsvDownload(1)}
                disabled={downloading}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60"
                style={{ backgroundColor: '#16a34a' }}
              >
                {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                CSV con Embalaje
              </button>
            </div>
          ) : (
            <button
              onClick={() => handleCsvDownload(0)}
              disabled={downloading}
              className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60"
              style={{ backgroundColor: '#16a34a' }}
            >
              {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {downloading ? 'Descargando...' : 'Descargar CSV'}
            </button>
          )
        )}
        {csvError && (
          <p className="text-sm text-red-600">{csvError}</p>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate('/pedidos')}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 text-white rounded-lg text-sm font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: PRIMARY }}
          >
            <ShoppingCart size={16} />
            Ver Mis Pedidos
          </button>
          <button
            onClick={() => navigate('/pedidos/nuevo')}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors hover:bg-gray-50"
            style={{ borderColor: PRIMARY, color: PRIMARY }}
          >
            <Plus size={16} />
            Crear Nuevo Pedido
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
